import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
import Input from 'components/atoms/input';
import forms from 'constants/forms';
import useUser from 'utils/useUser';
import {
  isAdmin,
  isAgencyDoctor,
  isPatientAcceptance,
  isRoleNameAdministrator,
} from 'utils/userTypeHelper';
import { fullNamePresenter } from 'utils';

const PAT_MANAGER = forms.adminPanelsUser.userEdit.careAgents[0];
const ONBOARDING_MANAGER = forms.adminPanelsUser.userEdit.careAgents[1];
const CCM_PAT_MANAGER = forms.adminPanelsUser.userEdit.careAgents[2];
const CCM_ADMISSION_MANAGER = forms.adminPanelsUser.userEdit.careAgents[3];
const CCM_MANAGER = forms.adminPanelsUser.userEdit.careAgents[4];

const getOptions = (careAgents) => {
  const options = careAgents.map((agent) => ({
    label: fullNamePresenter({
      title: agent.title,
      firstName: agent.firstName,
      lastName: agent.lastName,
    }),
    id: agent.guid,
  }));

  options.push({
    label: 'None',
    id: undefined,
  });

  return options;
};

const getSelectedOptions = (agents, type) =>
  agents
    .filter((ag) => ag.type === type)
    .map((ag) => ({
      label: `${ag.careAgentFirstName} ${ag.careAgentLastName}`,
      id: ag.careAgentGuid,
    }));

const CareAgent = ({ errors, agentOptions, selectedAgents }) => {
  const { userType, roleNames } = useUser();
  const isPatRole = isPatientAcceptance(roleNames);
  const isAdminRole = isRoleNameAdministrator(roleNames);
  const isAdminType = isAdmin(userType);
  const isHomeHealthType = isAgencyDoctor(userType);

  const canEditPatManager =
    isAdminType ||
    (isHomeHealthType && isAdminRole) ||
    (isHomeHealthType && isPatRole);
  const canEditOnboardingCmmManager =
    isAdminType || (isHomeHealthType && isAdminRole);

  const options = useMemo(() => {
    const result = {
      patManagers: [],
      ccmManagers: [],
      onboardingCcmManagers: [],
    };

    result.patManagers =
      canEditOnboardingCmmManager || canEditPatManager
        ? getOptions(agentOptions.hhaManagers)
        : getSelectedOptions(selectedAgents, PAT_MANAGER.id);

    result.ccmManagers = canEditOnboardingCmmManager
      ? getOptions(agentOptions.ccmManagers)
      : getSelectedOptions(selectedAgents, CCM_MANAGER.id);

    result.onboardingCcmManagers = canEditOnboardingCmmManager
      ? getOptions(agentOptions.hhaManagers)
      : getSelectedOptions(selectedAgents, ONBOARDING_MANAGER.id);

    return result;
  }, [agentOptions, selectedAgents]);

  return (
    <div className="admin-patient-form-block">
      <span>Care Agents</span>
      <Row>
        <Col lg="6" md="6" sm="6" xs="12">
          <Input
            {...PAT_MANAGER}
            disabled={!(canEditPatManager || canEditOnboardingCmmManager)}
            options={options.patManagers}
            dataCy={`admin-patient-edit-form__${PAT_MANAGER.id}`}
          />
          {errors[PAT_MANAGER.id] ? (
            <div className="input-error">{errors.doctorGuid}</div>
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col lg="6" md="6" sm="6" xs="12">
          <Input
            {...ONBOARDING_MANAGER}
            disabled={!canEditOnboardingCmmManager}
            options={options.onboardingCcmManagers}
            dataCy={`admin-patient-edit-form__${ONBOARDING_MANAGER.id}`}
          />
          {errors[ONBOARDING_MANAGER.id] ? (
            <div className="input-error">{errors.doctorGuid}</div>
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col lg="6" md="6" sm="6" xs="12">
          <Input
            {...CCM_PAT_MANAGER}
            disabled={!(canEditPatManager || canEditOnboardingCmmManager)}
            options={options.patManagers}
            dataCy={`admin-patient-edit-form__${CCM_PAT_MANAGER.id}`}
          />
          {errors[CCM_PAT_MANAGER.id] ? (
            <div className="input-error">{errors.doctorGuid}</div>
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col lg="6" md="6" sm="6" xs="12">
          <Input
            {...CCM_ADMISSION_MANAGER}
            disabled={!canEditOnboardingCmmManager}
            options={options.ccmManagers}
            dataCy={`admin-patient-edit-form__${CCM_ADMISSION_MANAGER.id}`}
          />
          {errors[CCM_ADMISSION_MANAGER.id] ? (
            <div className="input-error">{errors.doctorGuid}</div>
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col lg="6" md="6" sm="6" xs="12">
          <Input
            {...CCM_MANAGER}
            disabled={!canEditOnboardingCmmManager}
            options={options.ccmManagers}
            dataCy={`admin-patient-edit-form__${CCM_MANAGER.id}`}
          />
          {errors[CCM_MANAGER.id] ? (
            <div className="input-error">{errors.doctorGuid}</div>
          ) : null}
        </Col>
      </Row>
    </div>
  );
};

CareAgent.propTypes = {
  errors: PropTypes.shape(),
  agentOptions: PropTypes.shape({
    ccmManagers: PropTypes.array,
    hhaManagers: PropTypes.array,
  }),
  selectedAgents: PropTypes.array,
};

export default CareAgent;
